import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { api } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquareText, Send, Users, ArrowRight, ChevronRight } from "lucide-react";
import { ProjectScheduleKpi } from "./ProjectScheduleKpi";

const STATUS_TONE = {
  active: "bg-green-600 text-white border-green-600",
  in_progress: "bg-green-600 text-white border-green-600",
  yet_to_start: "bg-slate-600 text-white border-slate-600",
  on_hold: "bg-amber-500 text-white border-amber-500",
  hold: "bg-amber-500 text-white border-amber-500",
  completed: "bg-blue-600 text-white border-blue-600",
  cancelled: "bg-red-600 text-white border-red-600",
  hold_for_rfi: "bg-amber-500 text-white border-amber-500",
};
const STATUS_LABEL = { hold_for_rfi: "Hold for RFI" };
export const statusLabel = (s) => STATUS_LABEL[s] || (s || "-").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtH = (h) => `${Math.round((h || 0) * 10) / 10}h`;
const fmtDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "-");

export const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-3.5 py-1 rounded-full text-sm font-bold uppercase tracking-wide border shadow-sm ${STATUS_TONE[status] || STATUS_TONE.yet_to_start}`} data-testid="project-status-pill">
    {statusLabel(status)}
  </span>
);

const Kpi = ({ label, value, sub, children, testId }) => (
  <div className="px-6 py-5 border-r last:border-r-0 border-gray-100 min-w-0" data-testid={testId}>
    <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1 truncate">{value}</p>
    {children}
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Progress = ({ pct, tone }) => (
  <div className="h-1.5 w-full bg-gray-100 rounded-full mt-2 overflow-hidden">
    <div className={`h-full rounded-full transition-[width] duration-500 ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
  </div>
);

export const ProjectKpiStrip = ({ project, showCosts, showEstimated, totalValue, coValue, allocated, coHours, timesheet }) => {
  const totalAlloc = allocated + coHours;
  const pct = totalAlloc ? (timesheet.actual / totalAlloc) * 100 : 0;
  const cur = project.currency || "USD";
  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-kpi-strip">
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {showCosts && (
          <Kpi label="Project Value" testId="kpi-value" value={`${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${cur}`}
            sub={coValue ? `incl. ${coValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} from change orders` : "No approved change orders"} />
        )}
        <Kpi label="Allocated Hours" testId="kpi-allocated" value={fmtH(totalAlloc)}
          sub={coHours ? `${fmtH(allocated)} base + ${fmtH(coHours)} CO` : showEstimated ? `Estimated ${fmtH(project.estimated_hours)}` : "No change order hours"} />
        <Kpi label="Hours Used" testId="kpi-hours-used" value={`${fmtH(timesheet.actual)} / ${fmtH(totalAlloc)}`}
          sub={`Earned ${fmtH(timesheet.earned)}${totalAlloc ? ` · ${Math.round(pct)}% used` : ""}`}>
          <Progress pct={pct} tone={pct > 100 ? "bg-red-500" : pct > 85 ? "bg-amber-500" : "bg-emerald-500"} />
        </Kpi>
        <ProjectScheduleKpi project={project} />
      </div>
    </Card>
  );
};

const Fact = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 font-medium text-right min-w-0 break-words">{children || "-"}</span>
  </div>
);

export const ProjectFactsCard = ({ project, showCosts, coValue, coHours }) => (
  <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-facts-card">
    <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Details</CardTitle></CardHeader>
    <CardContent className="pt-0">
      <Fact label="Client Job #">{project.client_project_number}</Fact>
      <Fact label="Estimation">{project.estimation_number ? `${project.estimation_number}${project.estimation_version ? ` v${project.estimation_version}` : ""}` : null}</Fact>
      <Fact label="Branch">{Array.isArray(project.branch) ? project.branch.join(", ") : project.branch}</Fact>
      <Fact label="Awarded">{project.awarded_date ? fmtDate(project.awarded_date) : null}</Fact>
      {showCosts && (
        <>
          <Fact label="Base Price">{(project.base_price || 0).toLocaleString()} {project.currency || "USD"}</Fact>
          {coValue > 0 && <Fact label="Approved COs">+{coValue.toLocaleString()} {project.currency || "USD"}</Fact>}
          {project.currency === "CAD" && project.usd_base_price && <Fact label="Base (USD)">≈ {project.usd_base_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Fact>}
        </>
      )}
      {coHours > 0 && <Fact label="CO Hours">+{fmtH(coHours)}</Fact>}
      {project.description && <div className="pt-3 text-sm text-gray-600 whitespace-pre-wrap">{project.description}</div>}
    </CardContent>
  </Card>
);

export const ScopeCard = ({ scope }) => {
  const items = (scope || []).filter((s) => s?.name);
  if (!items.length) return null;
  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-scope-card">
      <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Scope of Work</CardTitle></CardHeader>
      <CardContent className="pt-0 space-y-2.5">
        {items.map((s) => (
          <div key={s.id || s.name} data-testid={`scope-item-${s.name}`}>
            <div className="flex justify-between text-sm">
              <span className="text-gray-800">{s.name}</span>
              <span className="text-gray-500 font-medium">{Math.round((s.weightage || s.total_weightage || 0) * 10) / 10}%</span>
            </div>
            <Progress pct={s.weightage || s.total_weightage || 0} tone="bg-indigo-400" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const TeamListCard = ({ projectId }) => {
  const [team, setTeam] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/projects/${projectId}/team`).then((r) => setTeam(r.data?.team || [])).catch(() => setTeam([]));
  }, [projectId]);
  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-team-list">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2"><Users className="h-4 w-4" /> Team {team && <span className="text-gray-400 font-normal text-sm">({team.length})</span>}</CardTitle>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/projects/${projectId}/planning`)} data-testid="team-manage-link">Manage <ArrowRight className="h-3 w-3 ml-1" /></Button>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {team === null ? <p className="text-sm text-gray-400">Loading…</p> : team.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="team-empty">No team members yet.</p>
        ) : team.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`team-member-${m.user_id}`}>
            <span className="truncate font-medium text-gray-800">{m.user_name}</span>
            <span className="flex flex-wrap gap-1 justify-end">
              {(m.roles || []).map((r) => (
                <Badge key={r} variant="outline" className="text-[10px] capitalize">{r === "other" && m.custom_role_name ? m.custom_role_name : r}</Badge>
              ))}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const RFI_TONE = { Open: "bg-blue-100 text-blue-800", "Partially Closed": "bg-yellow-100 text-yellow-800", Closed: "bg-green-100 text-green-800", "Follow-up": "bg-purple-100 text-purple-800" };
const TR_TONE = { Accepted: "bg-green-100 text-green-800", "Rejected with comments/Changes": "bg-red-100 text-red-800" };

const RfiRows = ({ rows }) => rows.map((r) => (
  <div key={r.id} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-b-0 text-sm" data-testid={`project-rfi-${r.id}`}>
    <span className="font-mono text-xs text-gray-500 w-28 shrink-0 pt-0.5">{r.full_rfi_number || r.rfi_number}</span>
    <span className="flex-1 min-w-0 text-gray-800 line-clamp-2">{r.description || r.item || "-"}</span>
    <span className={`text-[11px] px-2 py-0.5 rounded font-medium whitespace-nowrap ${RFI_TONE[r.status] || "bg-gray-100 text-gray-700"}`}>{r.status}</span>
  </div>
));

const TrRows = ({ rows }) => rows.map((t) => (
  <div key={t.id} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-b-0 text-sm" data-testid={`project-transmittal-${t.id}`}>
    <span className="font-mono text-xs text-gray-500 w-28 shrink-0 pt-0.5">{t.full_transmittal_number || t.transmittal_number}</span>
    <span className="flex-1 min-w-0 text-gray-800 line-clamp-2"><span className="text-gray-500">{t.stage}</span> · {t.description || "-"}</span>
    <span className="text-xs text-gray-500 whitespace-nowrap">{t.submittal_date || ""}</span>
    {t.status && <span className={`text-[11px] px-2 py-0.5 rounded font-medium whitespace-nowrap ${TR_TONE[t.status] || "bg-gray-100 text-gray-700"}`}>{t.status}</span>}
  </div>
));

export const RfiTransmittalCard = ({ projectId }) => {
  const [tab, setTab] = useState("rfi");
  const [expanded, setExpanded] = useState(false);
  const [rfis, setRfis] = useState(null);
  const [trs, setTrs] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/rfis?project_id=${projectId}`).then((r) => setRfis(r.data)).catch(() => setRfis([]));
    api.get(`/transmittals?project_id=${projectId}`).then((r) => setTrs(r.data)).catch(() => setTrs([]));
  }, [projectId]);
  const sortDesc = (a) => [...(a || [])].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
  const openRfis = (rfis || []).filter((r) => r.status !== "Closed").length;
  const rows = tab === "rfi" ? sortDesc(rfis) : sortDesc(trs);
  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-rfi-transmittal-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
        <button type="button" onClick={() => setExpanded((e) => !e)} className="p-1 rounded hover:bg-gray-100" aria-label="Toggle RFI and transmittals" data-testid="rfi-transmittal-toggle">
          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
        </button>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setExpanded(true); }}>
          <TabsList>
            <TabsTrigger value="rfi" data-testid="project-tab-rfis"><MessageSquareText className="h-3.5 w-3.5 mr-1" /> RFIs ({rfis?.length ?? 0}{openRfis ? ` · ${openRfis} open` : ""})</TabsTrigger>
            <TabsTrigger value="tr" data-testid="project-tab-transmittals"><Send className="h-3.5 w-3.5 mr-1" /> Transmittals ({trs?.length ?? 0})</TabsTrigger>
          </TabsList>
        </Tabs>
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(tab === "rfi" ? "/rfi-log" : "/transmittal-log")} data-testid="project-rfi-tr-open-log">Open log <ArrowRight className="h-3 w-3 ml-1" /></Button>
      </CardHeader>
      {expanded && <CardContent className="pt-0 max-h-80 overflow-y-auto">
        {(tab === "rfi" ? rfis : trs) === null ? <p className="text-sm text-gray-400">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-2" data-testid="project-rfi-tr-empty">{tab === "rfi" ? "No RFIs for this project." : "No transmittals for this project."}</p>
        ) : tab === "rfi" ? <RfiRows rows={rows} /> : <TrRows rows={rows} />}
      </CardContent>}
    </Card>
  );
};
